import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Flex, Text } from '@nula/core-ui';
import { formatElapsed } from './elapsed';

import { useLiveTranscriptStore } from './use-live-transcript-store';
import styles from './live-transcript.module.css';

interface LiveTranscriptProps {
  className?: string;
  /** Which recording's captions to show. The store is session-scoped and
   *  nothing clears it between recordings, so without this the previous
   *  meeting's transcript renders under the next one. Omitted by the HUD,
   *  which only ever exists for the recording it was opened for. */
  recordingId?: string;
}

export function LiveTranscript({ className, recordingId }: LiveTranscriptProps) {
  // Read the session-scoped store. The event subscription lives at each
  // window's root (recording-hud.tsx / LiveTranscriptBridge in router.tsx),
  // not here, so captions survive this component's unmount on tab switch /
  // HUD collapse.
  const activeTurn = useLiveTranscriptStore((s) => s.activeTurn);
  const committed = useLiveTranscriptStore((s) => s.committed);
  const storeId = useLiveTranscriptStore((s) => s.recordingId);
  const stale = recordingId !== undefined && storeId !== null && storeId !== recordingId;
  const started = useLiveTranscriptStore((s) => s.started) && !stale;
  const endRef = useRef<HTMLElement>(null);

  // Auto-scroll to the latest line whenever committed turns or the active turn
  // change. The scroll anchor <div ref> is a permitted raw element (no content,
  // purely a ref target).
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [committed.length, activeTurn]);

  if (!started) {
    return (
      <Flex vertical align="center" justify="center" className={clsx(styles.empty, className)}>
        <Text className={styles.emptyTitle}>Listening</Text>
        <Text type="secondary" size="sm" className={styles.emptyMessage}>
          The first words will appear here a moment after they are spoken.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex vertical className={clsx(styles.root, className)}>
      {/* A one-time explanation, not a status: it sat in the sticky row and
          competed with the captions on every scroll. */}
      <Text size="xs" type="secondary" className={styles.hint}>
        speakers finalized after the meeting
      </Text>

      {/* The rail: one hairline, the same weight that separates every other
          list in the app. Turns hang off it so time is the structure rather
          than a label sitting beside the text. */}
      <Flex className={styles.rail} aria-hidden />

      <Flex vertical className={styles.lane}>
        {committed.map((turn) => (
          <Flex key={turn.turnOrder} className={styles.turn}>
            <Text size="xs" className={styles.stamp}>
              {turn.startMs !== null ? formatElapsed(turn.startMs) : ''}
            </Text>
            <Flex data-node className={styles.node} aria-hidden />
            <Text className={styles.turnText}>{turn.text}</Text>
          </Flex>
        ))}

        {/* Provisional active turn — muted text + blinking caret via CSS */}
        {activeTurn && (
          <Flex className={clsx(styles.turn, styles.active)}>
            <Text size="xs" className={styles.stamp} />
            <Flex data-node data-live-node className={clsx(styles.node, styles.liveNode)} aria-hidden />
            <Text className={styles.turnText}>{activeTurn}</Text>
          </Flex>
        )}
      </Flex>

      <Flex ref={endRef} />
    </Flex>
  );
}
